import React, { useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { CollapsibleDropdown } from 'components';
import { ScorecardDataItem, ExtendedScorecardDataItem } from 'api';
import { getScorecardPinnedLeftColumnIds } from '../../../../consts';
import { processScorecardData } from '../../helpers/processScorecardData';
import { useScorecardColumns } from '../../hooks/useScorecardColumns';

interface ArchivedDataTableProps {
    archivedData: ScorecardDataItem[];
    selectedPeriod: string;
    dateRanges: { startDate: string; endDate: string; fiscalYear?: number }[];
    isAdmin: boolean;
    selectedUnit: 'INPUT_UNIT' | 'DEFAULT_UNIT';
    isEditModeEnabled?: boolean;
    userRoles: string[];
}

export const ArchivedDataTable = ({
    archivedData,
    selectedPeriod,
    dateRanges,
    isAdmin,
    selectedUnit,
    isEditModeEnabled = false,
    userRoles,
}: ArchivedDataTableProps) => {
    const [controlledExpanded, setControlledExpanded] = useState(false);

    const onToggle = () => setControlledExpanded(!controlledExpanded);

    const processedArchivedData = useMemo(() => processScorecardData(archivedData), [archivedData]);

    const columns = useScorecardColumns({
        selectedPeriod,
        dateRanges,
        isAdmin,
        selectedUnit,
        isOnEditMode: isEditModeEnabled,
        isArchivedTable: true,
        userRoles,
    });

    if (archivedData.length === 0) {
        return null;
    }

    return (
        <CollapsibleDropdown
            title="Archived Data"
            isExpanded={controlledExpanded}
            onToggle={onToggle}
            sx={{
                width: 'calc(100vw - 148px)',
                backgroundColor: '#E5EAF3',
                border: "1px !important",
                borderBottomLeftRadius: controlledExpanded ? "0px !important" : "10px !important",
                borderBottomRightRadius: controlledExpanded ? "0px !important" : "10px !important",
            }}
            titleSx={{ backgroundColor: '#E5EAF3', borderBottom: "0px !important", borderBottomLeftRadius: "10px", borderBottomRightRadius: "10px" }}
            contentSx={{ backgroundColor: '#E5EAF3' }}
            accordionSx={{
                border: isEditModeEnabled ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                borderTopLeftRadius: "0px !important",
                borderTopRightRadius: "0px !important",
                borderBottomLeftRadius: controlledExpanded ? "0px !important" : "10px !important",
                borderBottomRightRadius: controlledExpanded ? "0px !important" : "10px !important",
            }}
        >
            <MaterialReactTable
                enableTopToolbar
                enableStickyHeader
                muiTableContainerProps={{
                    sx: {
                        backgroundColor: '#E5EAF3',
                        border: "0px",
                        maxHeight: "calc(100vh - 460px)",
                        height: "auto",
                        width: "calc(100vw - 148px)",
                        overflow: "auto",
                        "& thead tr:first-of-type th:first-of-type": {
                            paddingLeft: "20px",
                        },
                        "& thead tr:first-of-type th:nth-of-type(2)": {
                            paddingLeft: "16px",
                        },
                        "& tbody tr": {
                            backgroundColor: '#E5EAF3',
                            height: "auto",
                            minHeight: "48px",
                            "&:hover": {
                                backgroundColor: '#E5EAF3 !important',
                            },
                            "&:hover td": {
                                backgroundColor: '#E5EAF3 !important',
                            },
                        },
                        "& tbody tr:hover": {
                            backgroundColor: '#E5EAF3 !important',
                        },
                        "& tbody tr:hover td": {
                            backgroundColor: '#E5EAF3 !important',
                        },
                        "& tbody tr td": {
                            backgroundColor: '#E5EAF3',
                            borderRight: isEditModeEnabled ? "1px solid #191FDB" : "1px solid #C0D0D4",
                            borderBottom: isEditModeEnabled ? "1px solid #191FDB" : "1px solid #C0D0D4",
                            "&:hover": {
                                backgroundColor: '#E5EAF3 !important',
                            },
                        },
                        "& tbody tr td:first-of-type": {
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            height: "auto",
                            minHeight: "48px",
                            padding: "12px 16px",
                            verticalAlign: "top",
                            "&:hover": {
                                backgroundColor: '#E5EAF3 !important',
                            },
                        },
                        "& tbody tr td:first-of-type p": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            margin: 0,
                            lineHeight: "1.4",
                        },
                        "& tbody tr:first-of-type td > p": {
                            justifyContent: "center",
                        },
                        "& tbody tr td:nth-of-type(2)": {
                            padding: "12px 16px",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            height: "auto",
                            minHeight: "48px",
                            verticalAlign: "top",
                            "&:hover": {
                                backgroundColor: '#E5EAF3 !important',
                            },
                        },
                        "& tbody tr td:nth-of-type(2) p": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            margin: 0,
                            lineHeight: "1.4",
                        },
                    },
                }}
                muiTablePaperProps={{
                    sx: {
                        boxShadow: "none",
                        backgroundColor: '#E5EAF3',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                    },
                }}
                muiTopToolbarProps={{
                    sx: {
                        height: '1px',
                        minHeight: '1px',
                        backgroundColor: '#E5EAF3',
                        '& .MuiIconButton-root': {
                            display: 'none',
                        },
                    },
                }}
                muiBottomToolbarProps={{
                    sx: {
                        display: "none",
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        lineHeight: "30px",
                        paddingRight: "16px",
                        "& .MuiBadge-root": {
                            display: "none",
                        },
                        backgroundColor: "#E5EAF3",
                        borderRight: isEditModeEnabled ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                        borderTop: isEditModeEnabled ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                        borderBottom: "none",
                    },
                }}
                muiTableBodyCellProps={({ row }) => ({
                    sx: {
                        display: 'flex',
                        flexWrap: 'wrap',
                        backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                            ? "#e8f5e1"
                            : "transparent",
                        "&:hover": {
                            backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                                ? "#e8f5e1 !important"
                                : "transparent !important",
                        },
                    }
                })}
                muiTableHeadRowProps={{
                    sx: {
                        "& > th:first-of-type": {
                            paddingLeft: "20px",
                        },
                        "& > th": {
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingLeft: "12px",
                            paddingRight: "16px",
                            borderRight: isEditModeEnabled ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                            borderTop: isEditModeEnabled ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                            borderBottom: "none",
                        },
                        height: "auto",
                        backgroundColor: "#E5EAF3",
                    },
                }}
                muiTableBodyRowProps={({ row }) => ({
                    sx: {
                        backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                            ? "#e8f5e1"
                            : '#E5EAF3',
                        height: "auto",
                        minHeight: "48px",
                        "&:hover": {
                            backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                                ? "#e8f5e1"
                                : '#E5EAF3 !important',
                        },
                        "&:first-of-type td": {
                            borderTop: isEditModeEnabled ? "1px solid #191FDB" : "1px solid #C0D0D4",
                        },
                    },
                })}
                columns={columns}
                data={processedArchivedData}
                enablePagination={false}
                enableRowVirtualization
                enableColumnResizing
                enablePinning
                initialState={{
                    density: 'compact',
                    columnPinning: { left: getScorecardPinnedLeftColumnIds(isAdmin) },
                }}
            />
        </CollapsibleDropdown>
    );
};
